<template>
  <v-dialog v-model="show" max-width="48rem" dark scrollable :fullscreen="$vuetify.breakpoint.xsOnly" content-class="extra-dialog">
    <v-card dark class="extra-card">
      <header class="dialog-header">
        <span class="header-icon"><v-icon>mdi-calendar-star</v-icon></span>
        <div>
          <div class="eyebrow">Leadership Coaching 1:1</div>
          <h2>Extra-Session anfragen</h2>
          <p>Gib zwei passende Wunschtermine an. Die Academy-Leitung meldet sich mit einer Bestätigung.</p>
        </div>
        <v-btn icon class="close-action" aria-label="Dialog schliessen" :disabled="saving" @click="show = false"><v-icon>mdi-close</v-icon></v-btn>
      </header>

      <v-card-text class="dialog-body">
        <v-form ref="form" v-model="valid">
          <div class="field-grid profile-grid">
            <v-text-field v-model="form.name" label="Name" outlined readonly :rules="requiredRules" />
            <v-text-field v-model="form.center" label="Center" outlined :rules="requiredRules" />
            <v-text-field v-model="form.function" label="Funktion" outlined :rules="requiredRules" />
          </div>

          <section class="form-section">
            <div class="section-heading">
              <span>1</span>
              <div><h3>Wunschtermine</h3><p>Bitte gib zwei unterschiedliche Optionen inklusive Dauer an.</p></div>
            </div>
            <div v-for="(option, index) in form.options" :key="index" class="option-row">
              <v-text-field
                v-model="option.startsAt"
                :label="`Wunsch ${index + 1}`"
                type="datetime-local"
                :min="minDateTime"
                step="1800"
                outlined
                :rules="dateRules" />
              <v-select
                v-model="option.durationMinutes"
                :items="durationOptions"
                item-text="label"
                item-value="value"
                label="Dauer"
                outlined
                :rules="requiredRules" />
            </div>
          </section>

          <section class="form-section">
            <div class="section-heading">
              <span>2</span>
              <div><h3>Dein Anliegen</h3><p>Ein kurzer Kontext hilft bei der Vorbereitung.</p></div>
            </div>
            <v-textarea
              v-model="form.caseDescription"
              label="Fallbeschreibung"
              hint="Beschreibe in wenigen Sätzen, worum es geht."
              persistent-hint
              outlined
              rows="4"
              counter="1200"
              :rules="textRules" />
            <v-textarea
              v-model="form.goals"
              label="Ziele und Erwartungen"
              hint="Was möchtest du aus der Extra-Session mitnehmen?"
              persistent-hint
              outlined
              rows="4"
              counter="1200"
              :rules="textRules" />
          </section>

          <v-alert v-if="error" type="error" outlined text class="form-alert">{{ error }}</v-alert>
        </v-form>
      </v-card-text>

      <v-card-actions class="dialog-actions">
        <v-btn text color="#d4d4d8" :disabled="saving" @click="show = false">Abbrechen</v-btn>
        <v-btn color="#009640" depressed class="submit-action" :loading="saving" :disabled="!valid" @click="submit">
          <v-icon left>mdi-send-outline</v-icon>Anfrage senden
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"

function localDateTime(date) {
  const pad = (value) => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export default {
  name: "LeadershipExtraRequestDialog",
  props: {
    value: { type: Boolean, default: false },
    minimumLeadHours: { type: Number, default: 0 },
  },
  data() {
    return {
      valid: false,
      saving: false,
      error: "",
      requiredRules: [(value) => Boolean(value) || "Pflichtfeld"],
      textRules: [
        (value) => Boolean(value && value.trim()) || "Pflichtfeld",
        (value) => !value || value.length <= 1200 || "Maximal 1200 Zeichen",
      ],
      durationOptions: [30, 60, 90, 120, 150, 180, 210, 240].map((value) => ({ label: `${value} Minuten`, value })),
      form: this.emptyForm(),
    }
  },
  computed: {
    leadHours() {
      const value = Number(this.minimumLeadHours)
      return Number.isFinite(value) && value >= 0 ? value : 0
    },
    dateRules() {
      return [
        (value) => Boolean(value) || "Bitte Datum und Zeit angeben",
        (value) => !value || new Date(value).getTime() >= Date.now() + this.leadHours * 60 * 60 * 1000 || `Der Termin braucht mindestens ${this.leadHours} Stunden Vorlauf`,
        (value) => !value || [0, 30].includes(new Date(value).getMinutes()) || "Bitte wähle eine Zeit auf :00 oder :30",
      ]
    },
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit("input", value)
      },
    },
    minDateTime() {
      const target = Date.now() + this.leadHours * 60 * 60 * 1000
      return localDateTime(new Date(Math.ceil(target / (30 * 60 * 1000)) * 30 * 60 * 1000))
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.prefill()
    },
  },
  mounted() {
    this.prefill()
  },
  methods: {
    emptyForm() {
      return {
        name: "",
        center: "",
        function: "",
        options: [
          { startsAt: "", durationMinutes: 60 },
          { startsAt: "", durationMinutes: 60 },
        ],
        caseDescription: "",
        goals: "",
      }
    },
    prefill() {
      const user = userService.getUser() || {}
      const studio = user.studio || user.studio_location || {}
      this.form.name = [user.vorname, user.nachname].filter(Boolean).join(" ") || user.name || ""
      this.form.center = typeof studio === "string" ? studio : studio.name || studio.studioName || studio.bezeichnung || this.form.center
      this.form.function = user.function || user.funktion || user.position || user.employeeFunction || this.form.function
      this.error = ""
    },
    async submit() {
      if (!this.$refs.form.validate()) return
      const first = new Date(this.form.options[0].startsAt).getTime()
      const second = new Date(this.form.options[1].startsAt).getTime()
      if (first === second) {
        this.error = "Bitte gib zwei unterschiedliche Wunschtermine an."
        return
      }
      this.saving = true
      this.error = ""
      try {
        const request = await api.akademie.createLeadershipExtraRequest({
          preferredOptions: this.form.options.map((option) => ({
            startsAt: new Date(option.startsAt).toISOString(),
            durationMinutes: option.durationMinutes,
          })),
          center: this.form.center.trim(),
          function: this.form.function.trim(),
          caseDescription: this.form.caseDescription.trim(),
          goals: this.form.goals.trim(),
        })
        this.$emit("submitted", request)
        this.form = this.emptyForm()
        this.prefill()
        this.show = false
      } catch (error) {
        this.error =
          (error.response && error.response.data && (error.response.data.error || error.response.data.message)) ||
          "Die Anfrage konnte nicht gesendet werden. Bitte versuche es erneut."
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.extra-card {
  color: #f4f4f5;
  background: #191919 !important;
  font-size: 1rem;
}

.dialog-header {
  display: grid;
  grid-template-columns: 3rem minmax(0, 1fr) 2.75rem;
  align-items: start;
  gap: 0.875rem;
  padding: 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.header-icon,
.close-action {
  display: grid;
  width: 2.75rem !important;
  min-width: 2.75rem !important;
  height: 2.75rem !important;
  place-items: center;
  border-radius: 0.875rem !important;
}

.header-icon {
  color: #c4b5fd;
  background: rgba(139, 92, 246, 0.14);
}

.header-icon .v-icon {
  color: currentColor;
}

.close-action {
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff !important;
  background: rgba(255, 255, 255, 0.05) !important;
}

.eyebrow {
  color: #7ee5a6;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.dialog-header h2 {
  margin: 0.1875rem 0 0.25rem;
  color: #fff;
  font-size: 1.375rem;
  line-height: 1.3;
}

.dialog-header p,
.section-heading p {
  margin: 0;
  color: #a3a3a8;
  font-size: 0.875rem;
  line-height: 1.5;
}

.dialog-body {
  padding: 1.25rem !important;
}

.field-grid {
  display: grid;
  gap: 0.75rem;
}

.profile-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-section {
  margin-top: 0.75rem;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.025);
}

.section-heading {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.section-heading > span {
  display: grid;
  flex: 0 0 2rem;
  width: 2rem;
  height: 2rem;
  place-items: center;
  border-radius: 0.6875rem;
  color: #7ee5a6;
  background: rgba(0, 150, 64, 0.13);
  font-size: 0.875rem;
  font-weight: 800;
}

.section-heading h3 {
  margin: 0 0 0.125rem;
  color: #fff;
  font-size: 1rem;
}

.option-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 11rem;
  gap: 0.75rem;
}

.dialog-actions {
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 1rem 1.25rem !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.submit-action {
  min-height: 2.75rem;
  border-radius: 0.8125rem !important;
  color: #fff !important;
  font-size: 0.875rem;
  font-weight: 750;
  text-transform: none;
}

.form-alert {
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
}

@media (max-width: 47.99rem) {
  .profile-grid,
  .option-row {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 37.49rem) {
  .dialog-header {
    grid-template-columns: 2.75rem minmax(0, 1fr) 2.75rem;
    padding: 1rem;
  }

  .dialog-header p {
    display: none;
  }

  .dialog-body {
    padding: 1rem !important;
  }

  .form-section {
    padding: 0.875rem;
  }

  .dialog-actions {
    flex-wrap: wrap;
  }

  .dialog-actions .v-btn {
    flex: 1 1 auto;
  }
}
</style>
